import { useTheme } from '@/hooks/useTheme';
import { useTranslation } from 'react-i18next';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { incidents as mockIncidents, formatMoneyShort, OwnerIncident, OwnerLostRevenue } from '@/mocks/ownerDashboard';

interface Props {
  incidents?: OwnerIncident[];
  lostRevenue?: OwnerLostRevenue | null;
  /** Hide money: drop the lost-revenue panel + per-incident UAH (dispatcher). */
  hideFinance?: boolean;
}

const STN_COLORS = ['#D12229', '#E0A030', '#7FB2E6', '#9B59B6', '#16A085', '#6B7280'];

export default function OperationsSection({ incidents: liveIncidents, lostRevenue, hideFinance }: Props) {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const incidents = liveIncidents ?? mockIncidents;
  const lr = lostRevenue ?? null;

  const cardBg = isDark ? '#1A1A1A' : '#FFFFFF';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = '#6B6B70';
  const textDim = isDark ? '#4A4A50' : '#9B9BA4';
  const innerBg = isDark ? '#0F0F0F' : '#F5F5F7';

  const fmtD = (iso?: string | null) => {
    const m = String(iso ?? '').match(/(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}.${m[2]}` : '';
  };
  const metaLine = (inc: OwnerIncident): string => {
    const parts: string[] = [];
    if (inc.kind) parts.push(inc.pct != null ? `${inc.kind} ${inc.pct}%` : inc.kind);
    if (inc.statusType === 'ongoing' && inc.since) {
      const days = Math.max(1, Math.round((Date.now() - new Date(inc.since).getTime()) / 86400000));
      parts.push(`з ${fmtD(inc.since)} · ${days} дн`);
    } else if (inc.endedAt) {
      parts.push(`усунено ${fmtD(inc.endedAt)}`);
    } else if (inc.since) {
      parts.push(fmtD(inc.since));
    }
    return parts.join(' · ');
  };

  // Leader-line callout label (зноска) for each donut segment: name + share %.
  const RADIAN = Math.PI / 180;
  const renderCallout = (p: any) => {
    const { cx, cy, midAngle, outerRadius, index } = p;
    const s = lr?.byStation[index];
    if (!s) return null;
    const color = STN_COLORS[index % STN_COLORS.length];
    const cos = Math.cos(-midAngle * RADIAN);
    const sin = Math.sin(-midAngle * RADIAN);
    const dir = cos >= 0 ? 1 : -1;
    // radial step out of the arc, then a horizontal run to a fixed side column
    const p0x = cx + outerRadius * cos, p0y = cy + outerRadius * sin;
    const p1x = cx + (outerRadius + 16) * cos, p1y = cy + (outerRadius + 16) * sin;
    const colX = cx + dir * (outerRadius + 46);
    const tx = colX + dir * 6;
    return (
      <g>
        <polyline points={`${p0x},${p0y} ${p1x},${p1y} ${colX},${p1y}`} stroke={color} strokeWidth={1.2} fill="none" />
        <circle cx={colX} cy={p1y} r={2} fill={color} />
        <text x={tx} y={p1y} textAnchor={dir > 0 ? 'start' : 'end'} fontFamily="Muller, Inter, sans-serif">
          <tspan x={tx} dy="-0.32em" fontSize="10" fontWeight={600} fill={textMain}>{s.name}</tspan>
          <tspan x={tx} dy="1.3em" fontSize="12.5" fontWeight={700} fill={color}>{s.sharePct}%</tspan>
        </text>
      </g>
    );
  };

  return (
    <div className={`grid grid-cols-1 ${hideFinance ? '' : 'lg:grid-cols-[1.3fr_1fr]'} gap-4 mb-5`}>
      {/* LEFT: Incidents */}
      <div
        className="rounded-xl p-5"
        style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
      >
        <div className="flex items-center justify-between mb-2.5">
          <div className="text-sm font-semibold" style={{ color: textMain, fontFamily: 'Muller, Inter, sans-serif' }}>
            {t('d_ops_incidents')}
          </div>
          <div className="text-[10px] font-semibold" style={{ color: textMuted, fontFamily: 'Muller, Inter, sans-serif' }}>
            {t('d_ops_active')}: {incidents.filter((x) => x.statusType === 'ongoing').length}
          </div>
        </div>
        {incidents.length === 0 && (
          <div className="text-xs px-3 py-4 rounded-lg text-center" style={{ background: innerBg, color: '#0D7A3C', fontFamily: 'Muller, Inter, sans-serif' }}>
            ✓ {t('d_ops_no_incidents')}
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          {incidents.map((inc, i) => {
            const dotColor = inc.severity === 'red' ? '#D12229' : '#F59E0B';
            return (
              <div
                key={i}
                className="flex items-start gap-2 px-2.5 py-1.5 rounded-lg"
                style={{ background: innerBg }}
              >
                <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                  <i className={inc.icon} style={{ color: dotColor, fontSize: 14 }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-medium leading-snug" style={{ color: textMain, fontFamily: 'Muller, Inter, sans-serif' }}>
                    <strong>{inc.station}</strong> — {inc.description}
                  </div>
                  {metaLine(inc) && (
                    <div className="text-[10px] mt-0.5" style={{ color: textDim, fontFamily: 'Muller, Inter, sans-serif' }}>
                      {metaLine(inc)}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!hideFinance && inc.impactUah > 0 && (
                    <span className="text-xs font-semibold whitespace-nowrap" style={{ color: '#D12229', fontFamily: 'Muller, Inter, sans-serif' }}>
                      −{formatMoneyShort(inc.impactUah)} {t('d_u_uah')}
                    </span>
                  )}
                  <span
                    className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
                    style={{
                      background: inc.statusType === 'resolved' ? '#0D7A3C20' : '#F59E0B20',
                      color: inc.statusType === 'resolved' ? '#0D7A3C' : '#F59E0B',
                      fontFamily: 'Muller, Inter, sans-serif',
                    }}
                  >
                    {inc.status}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* RIGHT: Lost Revenue — ratio charts (hidden on dispatcher dashboard) */}
      {!hideFinance && (
      <div
        className="rounded-xl p-5 flex flex-col"
        style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
      >
        <div className="text-sm font-semibold mb-0.5" style={{ color: textMain, fontFamily: 'Muller, Inter, sans-serif' }}>
          {t('d_ops_lost_value')}
        </div>
        <div className="text-xs mb-3" style={{ color: textDim, fontFamily: 'Muller, Inter, sans-serif' }}>
          {t('d_ops_lost_sub')}
        </div>

        {lr && lr.byStation.length > 0 ? (
          <>
            {/* ratio by station — large donut, side-oriented leader-line callouts */}
            <div className="relative" style={{ width: '100%', height: 232 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 12, right: 14, bottom: 12, left: 14 }}>
                  <Pie data={lr.byStation} dataKey="uah" nameKey="name" cx="50%" cy="50%"
                       innerRadius={52} outerRadius={78} paddingAngle={2} stroke="none"
                       isAnimationActive={false} labelLine={false} label={renderCallout}>
                    {lr.byStation.map((_, i) => <Cell key={i} fill={STN_COLORS[i % STN_COLORS.length]} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-lg font-bold leading-none tabular-nums" style={{ color: '#D12229', fontFamily: 'Muller, Inter, sans-serif', letterSpacing: '-0.02em' }}>
                  {formatMoneyShort(lr.todayUah)}
                </span>
                <span className="text-[9px] mt-1" style={{ color: textMuted }}>{t('d_ops_uah_day')}</span>
              </div>
            </div>

            {/* ratio by cause — stacked bar */}
            <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${cardBorder}` }}>
              <div className="text-[10px] mb-1.5" style={{ color: textMuted, fontFamily: 'Muller, Inter, sans-serif' }}>{t('d_ops_by_cause')}</div>
              <div className="w-full h-2 rounded-full flex overflow-hidden" style={{ background: innerBg }}>
                {lr.byCause.map((c, i) => (
                  <div key={i} className="h-full" style={{ width: `${c.sharePct}%`, background: c.name === 'Відключення' ? '#D12229' : '#E0A030' }} />
                ))}
              </div>
              <div className="flex gap-3 mt-1.5 flex-wrap">
                {lr.byCause.map((c, i) => (
                  <span key={i} className="flex items-center gap-1.5 text-[10px]" style={{ color: textDim, fontFamily: 'Muller, Inter, sans-serif' }}>
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: c.name === 'Відключення' ? '#D12229' : '#E0A030' }} />
                    {c.name} {c.sharePct}%
                  </span>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-xs py-6" style={{ color: '#0D7A3C', fontFamily: 'Muller, Inter, sans-serif' }}>
            ✓ {t('d_ops_no_losses')}
          </div>
        )}
      </div>
      )}
    </div>
  );
}